import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Home,
  ShoppingBag,
  Map as MapIcon,
  Smartphone,
  Bell,
  Keyboard,
  Volume2,
  Plus,
  Minus,
  RotateCcw,
  Link2,
  ChevronRight,
  X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@london-life/ui/components/dialog';
import {
  airportsOf,
  NEEDS,
  NEED_KEYS,
  RIVER_PLOTS,
  formatPence,
  moodOf,
  type CityActivity,
  type Needs,
  type PlaceHereReply,
  type PlayState,
  type TravelMode,
  type GovBanner,
} from '@london-life/contracts';
import type { Character } from '@london-life/game/game';
import type { MapPoint } from '@london-life/game/city/types';
import { cityActivitiesOf } from '@london-life/game/city/activities';
import { useContent } from '../lib/content';
import { useTravelChoices } from '../lib/travel';
import { useGovBanner } from '../lib/gov';
import { AIRPORT_FOCUS, BILLBOARD_FOCUS, CITIES, MAP_CHIPS } from '@london-life/game/city/layout';
import type { MapAdverts } from '../adverts/use-adverts';
import { api } from '../lib/api';
import type { MapCommand } from '@london-life/game/city/types';
import { DistrictMap, type MapJourney } from './district-map';
import { PersonPicture } from '../people/person-card';
import { needColour } from '../play/needs';
import { useSiteStats } from './use-site-stats';
import { PhoneScreen, type PhoneApp } from '../phone/phone-screen';
import { notify } from '../lib/alerts';
import { useBackLayer, useOverlay } from '../lib/back-stack';

type Props = {
  onStart: () => void;
  returning: boolean;
  // the player's wallet once they have a character
  walletPence?: number;
  // the live game state once the player has a character: where they are and how they feel
  play?: { placeId: string; needs: Needs; car?: PlayState['car'] } | null;
  onWalk?: () => void;
  // the furniture shop (in the player's house)
  onShop?: () => void;
  // a player's phone: its apps (Messages and the rest), and the count shown on the dock
  phoneApps?: PhoneApp[];
  phoneBadge?: number;
  // the bell: notices waiting, and what opening it does
  bell?: { unread: number; onOpen: () => void };
  // bumped to open the phone again (coming back from Messages)
  phoneOpenTick?: number;
  onGo?: (place: CityActivity, mode: TravelMode) => void;
  // an airport's pin opens Departures (as Lagos Life's); `terminal` opens the place's own card instead
  onDepartures?: (place: CityActivity, terminal: () => void) => void;
  // a returning player taps King's Cross: its ticket office (the station itself is a link on it)
  onTickets?: (place: CityActivity, terminal: () => void) => void;
  dataSaver?: boolean;
  // a returning player's character and needs open their profile
  onProfile?: (tab: 'profile' | 'needs') => void;
  // the dock shows on every screen: its buttons close the open screen first, and light the one in use
  onDock?: () => void;
  dockActive?: 'home' | 'buy' | 'phone' | null;
  // the signed-in player's account pill, placed in the HUD so phones can stack it with the rest
  account?: ReactNode;
  // a journey under way: shown on the map (the player walking, or their bus, cab or train)
  journey?: { from: string; to: string; mode: TravelMode; startedAt: number; endsAt: number } | null;
  // the player's character and @name (walking on the map, the tag over them)
  traveller?: { character: Character; tag: string } | null;
  // the Neighbours list (homes to visit), for a player with a character
  onNeighbours?: () => void;
  // players' shops (for a player with a character)
  onShops?: () => void;
  // Gov: the Mayor's banner opens it (for a player with a character)
  onGov?: () => void;
  // what the character is doing (shown under the status bar, as in Lagos Life)
  actionQueue?: ReactNode;
  // the players' ads on the boards, and a tap on one
  adverts?: MapAdverts | null;
  onBoard?: (board: number) => void;
  // another screen asks for a place's card (Health: "Go to the clinic"); a new tick opens it again
  placeRequest?: { placeId: string; tick: number } | null;
};
// the map's "Names" switch is remembered on this device
const NAMES_KEY = 'll.map.names';
const remembered = () => {
  try {
    return localStorage.getItem(NAMES_KEY) === '1';
  } catch {
    return false;
  }
};
// a new arrival's needs before they have a character (shown on the public map)
const PREVIEW_NEEDS: Needs = { hunger: 82, energy: 95, hygiene: 90, fun: 65, social: 58, bladder: 88 };
// from the palette in styles.css, so a theme change reaches the bars too

// show the London clock the way the reference shows the city time
function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);
  const hour = now.getHours();
  return {
    icon: hour >= 7 && hour < 19 ? '☀️' : '🌙',
    text: `${now.toLocaleDateString('en-GB', { weekday: 'short' })} ${now.getDate()} · ${now.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase()}`,
  };
}

// make the map the first view of London Life
export default function PublicHomepage({
  onStart,
  returning,
  walletPence,
  play,
  onWalk,
  onGo,
  onDepartures,
  onTickets,
  dataSaver = false,
  onProfile,
  account,
  onShop,
  phoneApps,
  phoneBadge = 0,
  bell,
  phoneOpenTick = 0,
  onDock,
  dockActive = null,
  journey = null,
  traveller = null,
  onNeighbours,
  onShops,
  onGov,
  actionQueue,
  adverts,
  onBoard,
  placeRequest = null,
}: Props) {
  const needs = play?.needs ?? PREVIEW_NEEDS;
  // online now and visits, as the super admin chooses to show them (or not at all)
  const stats = useSiteStats();
  const count = (value: number) => value.toLocaleString('en-GB');
  const mood = moodOf(needs);
  // the places come from the published content (the admin edits them)
  const content = useContent();
  const activities = useMemo(
    () => (content.catalogue ? cityActivitiesOf(content.catalogue.places) : []),
    [content.catalogue],
  );
  // the real Prime Minister until the game holds its own elections (kept by the super admin)
  const [primeMinister, setPrimeMinister] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void api
      .worldFacts()
      .then((data) => alive && setPrimeMinister(data.world.primeMinister.name))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  // the Mayor of London this week, the policy in force and their announcement (Gov), on everyone's map
  const govBanner = useGovBanner();
  // the ways to travel the admin published (the starting ones until the content has loaded), and the player's car
  const transportOptions = useTravelChoices(play?.car);
  const positionOf = (id: string | undefined) => activities.find((place) => place.id === id)?.position ?? null;
  const home = positionOf(play?.placeId);
  const from = positionOf(journey?.from),
    to = positionOf(journey?.to);
  const mapJourney: MapJourney | null =
    journey && traveller && from && to
      ? { from, to, mode: journey.mode, startedAt: journey.startedAt, endsAt: journey.endsAt, ...traveller }
      : null;
  // icons only on the map, unless "Names" is on
  const [showNames, setShowNames] = useState(remembered);
  const toggleNames = () => {
    const next = !showNames;
    setShowNames(next);
    try {
      localStorage.setItem(NAMES_KEY, next ? '1' : '0');
    } catch {
      // private browsing: on for now only
    }
  };
  const [selected, setSelected] = useState<CityActivity | null>(null);
  // who is at the chosen place right now (players only; a visitor sees the place without them)
  const [here, setHere] = useState<PlaceHereReply | null>(null);
  const selectedId = selected?.id ?? null;
  useEffect(() => {
    setHere(null);
    if (!selectedId || !returning) return;
    let alive = true;
    void api.people
      .here(selectedId)
      .then((reply) => alive && setHere(reply))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [selectedId, returning]);
  // what can be done at the chosen place, from the published content (its four headline things before that)
  const doing = selected ? (content.catalogue?.activitiesAt(selected.id) ?? []) : [];
  const [transport, setTransport] = useState<TravelMode>('walk');
  const [panel, setPanel] = useState<'places' | 'help' | null>(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const hasPhone = !!phoneApps;
  const closePhone = useCallback(() => setPhoneOpen(false), []);
  useEffect(() => {
    if (phoneOpenTick && hasPhone) setPhoneOpen(true);
  }, [phoneOpenTick, hasPhone]);
  // visitors have no phone yet: the places list stands in for it
  const openPhone = () => (phoneApps ? setPhoneOpen(true) : setPanel('places'));
  // every app closes the phone first; Places is the phone's own map list
  const placesApp: PhoneApp = {
    id: 'places',
    label: 'Places',
    icon: '📍',
    tone: 'red',
    onOpen: () => setPanel('places'),
  };
  const apps: PhoneApp[] = phoneApps
    ? [...phoneApps, placesApp].map((app) => ({ ...app, onOpen: () => (setPhoneOpen(false), app.onOpen()) }))
    : [];
  const [focus, setFocus] = useState<{ id?: string; position: MapPoint } | null>(null);
  const [command, setCommand] = useState<{ action: MapCommand; sequence: number }>({ action: 'reset', sequence: 0 });
  const clock = useClock();
  const moveCamera = (action: MapCommand) => setCommand((previous) => ({ action, sequence: previous.sequence + 1 }));
  // the phone's back gesture closes these too
  useBackLayer(phoneOpen, () => setPhoneOpen(false));
  useBackLayer(panel !== null, () => setPanel(null));
  // the place card: back or Escape closes it, and only it (Jerry, 30 Oct)
  useOverlay(selected !== null, () => setSelected(null));
  const showMap = () => {
    onDock?.();
    setPhoneOpen(false);
    setSelected(null);
    setPanel(null);
    setFocus(null);
    moveCamera('reset');
  };
  const active = phoneOpen ? 'phone' : (dockActive ?? 'map');
  const dockClass = (id: typeof active) => (active === id ? 'active' : undefined);
  // answer the keys listed in the help card
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.metaKey ||
        event.ctrlKey
      )
        return;
      const key = event.key.toLowerCase();
      if (key === 'm') showMap();
      else if (key === 'h') {
        onDock?.();
        onStart();
      } else if (key === 'p') {
        onDock?.();
        if (hasPhone) setPhoneOpen(true);
        else setPanel('places');
      } else if (key === '+' || key === '=') moveCamera('in');
      else if (key === '-') moveCamera('out');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // route every control through the same few actions
  const showPlace = (place: CityActivity) => {
    setPanel(null);
    setTransport(transportOptions[0]?.mode ?? 'walk');
    setSelected(place);
  };
  const openPlace = (place: CityActivity) => {
    const airport =
      content.catalogue && airportsOf(content.catalogue.airport).some((stop) => stop.placeId === place.id);
    if (returning && onDepartures && airport) {
      setPanel(null);
      setSelected(null);
      return onDepartures(place, () => showPlace(place));
    }
    if (returning && onTickets && content.catalogue?.rail.placeId === place.id) {
      setPanel(null);
      setSelected(null);
      return onTickets(place, () => showPlace(place));
    }
    showPlace(place);
  };
  const flyTo = (id: string) => {
    const place = activities.find((item) => item.id === id);
    if (place) setFocus({ id: place.id, position: place.position });
  };
  const requestTick = placeRequest?.tick ?? 0;
  useEffect(() => {
    if (!requestTick || !placeRequest) return;
    const place = activities.find((item) => item.id === placeRequest.placeId);
    if (!place) return;
    setPhoneOpen(false);
    setFocus({ id: place.id, position: place.position });
    openPlace(place);
    // a new request (its tick) opens the card; the place list loading later does not
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestTick]);
  // the river plots: the camera goes over them and their sheet opens (who is there, buy a plot)
  const openPlots = (open: (board: number) => void) => {
    flyTo('plots');
    open(RIVER_PLOTS.board);
  };
  const walk = () => {
    setSelected(null);
    if (returning && onWalk) onWalk();
    else onStart();
  };
  const go = () => {
    if (!selected) return;
    const place = selected;
    setSelected(null);
    // a player sets off from where they are (the map then follows the journey); a visitor sees the place
    if (returning && onGo) {
      if (home) setFocus({ position: home });
      window.setTimeout(() => onGo(place, transport), 500);
    } else {
      setFocus({ id: place.id, position: place.position });
      window.setTimeout(onStart, 950);
    }
  };
  const share = async (place: CityActivity) => {
    const url = `${location.origin}${location.pathname}#${place.id}`;
    try {
      await navigator.clipboard.writeText(url);
      notify('Link copied');
    } catch {
      notify(url);
    }
  };
  const fare = transportOptions.find((item) => item.mode === transport) ?? transportOptions[0];

  return (
    <main className="public-city">
      <div className={`city-map-layer${selected || panel ? ' is-blurred' : ''}`}>
        {/* drawn once the places are known (or could not be loaded), so no building stands on a place */}
        {content.status !== 'loading' && (
          <DistrictMap
            activities={activities}
            command={command}
            focus={focus}
            onPlace={openPlace}
            dataSaver={dataSaver}
            showNames={showNames}
            home={home}
            journey={mapJourney}
            adverts={adverts}
            onBoard={onBoard}
            jet={content.catalogue?.airport.airline ?? null}
            trains={content.catalogue?.rail.lines.filter((line) => line.visible).map((line) => line.colours) ?? null}
          />
        )}
      </div>

      {/* the top-left HUD: one stacked column on phones, the usual spread on wider screens */}
      <div className="city-hud">
        <header className="city-status" aria-label="City status">
          <span className="city-status-main">
            <span>
              {clock.icon} {clock.text}
            </span>
            <i />
            <span className="city-mood">{returning ? `${mood.icon} ${mood.label}` : '👋 New in town'}</span>
          </span>
          {stats?.show && (stats.online !== null || stats.people !== null) && (
            <>
              <i className="city-stats-divider" />
              <span className="city-stats" aria-label="Players online and people who have visited">
                {stats.online !== null && <span className="city-online">● {count(stats.online)} online</span>}
                {stats.people !== null && <span className="city-people">{count(stats.people)} visitors</span>}
              </span>
            </>
          )}
          {primeMinister && (
            <>
              <i className="city-pm-divider" />
              <span className="city-pm" title={`Prime Minister: ${primeMinister}`}>
                🏛️ PM {primeMinister}
              </span>
            </>
          )}
        </header>
        {govBanner?.mayor && <MayorBanner banner={govBanner} onOpen={onGov} />}
        <div className="city-wallet">
          <button type="button" aria-label="Sound" className="city-icon">
            <Volume2 size={17} />
          </button>
          <strong>{walletPence === undefined ? '£0' : formatPence(walletPence)}</strong>
          <button
            type="button"
            className="city-plus"
            aria-label={returning ? 'Continue' : 'Create your character'}
            onClick={onStart}
          >
            <Plus size={18} />
          </button>
        </div>
        <button
          type="button"
          className="city-bell"
          aria-label={bell?.unread ? `Notifications, ${bell.unread} new` : 'Notifications'}
          onClick={() => (bell ? bell.onOpen() : setPanel('places'))}
        >
          <Bell size={18} />
          {!!bell?.unread && <b>{bell.unread > 99 ? '99+' : bell.unread}</b>}
        </button>
        {actionQueue && <div className="city-doing">{actionQueue}</div>}
        <nav className="city-chips" aria-label="Map shortcuts">
          {MAP_CHIPS.filter((chip) => chip.id !== 'shops' || onShops).map((chip) => (
            <button
              type="button"
              key={chip.id}
              onClick={() =>
                chip.id === 'billboards'
                  ? setFocus({ position: BILLBOARD_FOCUS })
                  : chip.id === 'airport'
                    ? setFocus({ position: AIRPORT_FOCUS })
                    : chip.id === 'river' && onBoard
                      ? openPlots(onBoard)
                      : chip.id === 'neighbours' && onNeighbours
                        ? onNeighbours()
                        : chip.id === 'shops' && onShops
                          ? onShops()
                          : chip.place
                            ? flyTo(chip.place)
                            : walk()
              }
            >
              <span>{chip.icon}</span>
              {chip.label}
            </button>
          ))}
        </nav>
        <nav className="city-cities" aria-label="Cities">
          {CITIES.map((city) => (
            <button
              type="button"
              key={city.id}
              className={city.live ? 'active' : 'soon'}
              aria-label={city.live ? undefined : `${city.name} (coming soon)`}
              aria-current={city.live ? 'page' : undefined}
              onClick={() => {
                if (!city.live) notify(`${city.name} is coming soon`);
              }}
            >
              <span>{city.icon}</span>
              <em className="city-name">{city.name}</em>
            </button>
          ))}
          <button type="button" className="city-names" aria-pressed={showNames} onClick={toggleNames}>
            <span>🏷️</span>
            Names
          </button>
        </nav>
        {account}
      </div>

      <nav className="city-map-controls" aria-label="Map controls">
        <button type="button" aria-label="Zoom in" onClick={() => moveCamera('in')}>
          <Plus size={18} />
        </button>
        <button type="button" aria-label="Zoom out" onClick={() => moveCamera('out')}>
          <Minus size={18} />
        </button>
        <button
          type="button"
          aria-label="Reset map view"
          onClick={() => {
            setFocus(null);
            moveCamera('reset');
          }}
        >
          <RotateCcw size={16} />
        </button>
      </nav>

      <div className="city-player">
        <button
          type="button"
          className="city-avatar"
          aria-label={returning ? 'Your profile' : 'Create your character'}
          onClick={() => (onProfile ? onProfile('profile') : onStart())}
        >
          <span>🧑🏾</span>
        </button>
        <button
          type="button"
          className="city-needs"
          aria-label="Needs"
          onClick={() => onProfile?.('needs')}
          disabled={!onProfile}
        >
          {NEED_KEYS.map((key) => (
            <span key={key} title={`${NEEDS[key].label} ${Math.round(needs[key])}%`}>
              <em>{NEEDS[key].icon}</em>
              <i>
                <b style={{ width: `${needs[key]}%`, background: needColour(needs[key]) }} />
              </i>
            </span>
          ))}
        </button>
      </div>
      <nav className="city-dock" aria-label="Game menu">
        <button
          type="button"
          className={dockClass('home')}
          aria-current={active === 'home' ? 'page' : undefined}
          onClick={() => {
            setPhoneOpen(false);
            onDock?.();
            onStart();
          }}
        >
          <Home size={21} />
          <span>Home</span>
        </button>
        <button
          type="button"
          className={dockClass('buy')}
          aria-current={active === 'buy' ? 'page' : undefined}
          onClick={() => {
            setPhoneOpen(false);
            onDock?.();
            onShop?.();
          }}
          disabled={!onShop}
          aria-disabled={!onShop || undefined}
        >
          <ShoppingBag size={21} />
          <span>Buy</span>
        </button>
        <button
          type="button"
          className={dockClass('map')}
          aria-current={active === 'map' ? 'page' : undefined}
          onClick={showMap}
        >
          <MapIcon size={21} />
          <span>Map</span>
        </button>
        <button
          type="button"
          className={dockClass('phone')}
          aria-current={active === 'phone' ? 'page' : undefined}
          onClick={() => {
            onDock?.();
            openPhone();
          }}
        >
          <Smartphone size={21} />
          <span>Phone</span>
          {phoneBadge > 0 && <b>{phoneBadge > 99 ? '99+' : phoneBadge}</b>}
        </button>
      </nav>
      <button type="button" className="city-keys" aria-label="Keyboard shortcuts" onClick={() => setPanel('help')}>
        <Keyboard size={18} />
      </button>

      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent className="city-card" showCloseButton={false}>
          {selected && (
            <>
              <div className="city-card-head">
                <span className="city-card-icon">{selected.icon}</span>
                <div>
                  <DialogTitle>{selected.name}</DialogTitle>
                  <small>{selected.area}</small>
                </div>
                <button type="button" className="city-card-close" aria-label="Close" onClick={() => setSelected(null)}>
                  <X size={18} />
                </button>
              </div>
              <DialogDescription>{selected.description}</DialogDescription>
              <button type="button" className="city-card-share" onClick={() => void share(selected)}>
                <Link2 size={14} /> Share a link to {selected.name}
              </button>
              <div className="city-card-things" aria-label="What you can do here">
                {doing.length
                  ? doing.map((activity) => (
                      <span key={activity.id}>
                        <i aria-hidden="true">{activity.icon}</i> {activity.label}
                      </span>
                    ))
                  : selected.things.map((thing) => <span key={thing}>{thing}</span>)}
              </div>
              {here && here.count > 0 && (
                <div className="city-card-here" aria-label="Here now">
                  <small>Here now</small>
                  <ul>
                    {here.people.map((person) => (
                      <li key={person.lifeId}>
                        <PersonPicture person={person} />
                        {person.username ? `@${person.username}` : person.firstName}
                      </li>
                    ))}
                    {here.count > here.people.length && <li>+{here.count - here.people.length} more</li>}
                  </ul>
                </div>
              )}
              <button type="button" className="city-card-row" onClick={() => setPanel('places')}>
                <span>🛍️</span>Player shops here
                <ChevronRight size={16} />
              </button>
              {selected.comingSoon ? (
                <div className="city-card-soon">
                  Opening in a later step. Shift work, forklifts and the canteen are on the way.
                </div>
              ) : (
                <>
                  <div className="city-card-transport" role="radiogroup" aria-label="How to get there">
                    {transportOptions.map((item) => (
                      <button
                        type="button"
                        key={item.mode}
                        role="radio"
                        aria-checked={transport === item.mode}
                        className={transport === item.mode ? 'active' : ''}
                        onClick={() => setTransport(item.mode)}
                      >
                        <span>{item.icon}</span>
                        <strong>{item.label}</strong>
                        <small>{item.farePence ? formatPence(item.farePence) : (item.note ?? 'Free')}</small>
                      </button>
                    ))}
                  </div>
                  <button type="button" className="city-card-go" onClick={go}>
                    {!returning
                      ? 'Create your character to go'
                      : play?.placeId === selected.id
                        ? 'You are here · see what to do'
                        : `Go · ${fare?.farePence ? formatPence(fare.farePence) : 'Free'}`}
                  </button>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={panel === 'places'}
        onOpenChange={(open) => {
          if (!open) setPanel(null);
        }}
      >
        <DialogContent className="city-card city-phone">
          <DialogTitle>Places</DialogTitle>
          <DialogDescription>
            {returning
              ? 'Pick a place to open it on the map.'
              : 'Welcome to London. Pick a place to see what you can do there.'}
          </DialogDescription>
          <div className="city-place-list">
            {activities
              .filter((place) => place.visible)
              .map((place) => (
                <button type="button" key={place.id} onClick={() => openPlace(place)}>
                  <span className="city-card-icon">{place.icon}</span>
                  <span>
                    <strong>{place.name}</strong>
                    <small>{place.area}</small>
                  </span>
                  <ChevronRight size={16} />
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={panel === 'help'}
        onOpenChange={(open) => {
          if (!open) setPanel(null);
        }}
      >
        <DialogContent className="city-card city-phone">
          <DialogTitle>Finding your way</DialogTitle>
          <DialogDescription>
            Drag the map to move around. Scroll or pinch to zoom. Tap a marker to see what you can do there, choose how
            to travel and press Go.
          </DialogDescription>
          <div className="city-help">
            <div>
              <kbd>M</kbd> Map
            </div>
            <div>
              <kbd>H</kbd> Home
            </div>
            <div>
              <kbd>P</kbd> Phone
            </div>
            <div>
              <kbd>+</kbd>
              <kbd>−</kbd> Zoom
            </div>
            <div>
              <kbd>Esc</kbd> Close
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {hasPhone && <PhoneScreen open={phoneOpen} apps={apps} onClose={closePhone} />}
    </main>
  );
}

// the Mayor on the map (as Lagos Life's Governor): who, the policy in force, and their announcement; tapping it
// opens Gov for a player with a character
function MayorBanner({ banner, onOpen }: { banner: GovBanner; onOpen: (() => void) | undefined }) {
  const { mayor, policy } = banner;
  if (!mayor) return null;
  const words = (
    <>
      <span>
        🏛️ Mayor {mayor.name}
        {policy ? ` · ${policy.icon} ${policy.label}` : ''}
      </span>
      {mayor.announcement && <small>📢 “{mayor.announcement.text}”</small>}
    </>
  );
  return onOpen ? (
    <button type="button" className="city-gov" aria-label={`Mayor ${mayor.name}: open Gov`} onClick={onOpen}>
      {words}
    </button>
  ) : (
    <div className="city-gov" aria-label={`Mayor ${mayor.name}`}>
      {words}
    </div>
  );
}
